import { lazy, Suspense } from "react";
import "@/App.css";
// Side-effect import: registers global axios interceptors (auth headers +
// idempotency keys) for every HTTP call across the app. MUST be imported
// before any component that does network IO.
import "@/utils/api";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Dashboard from "@/pages/Dashboard";
import LoginPage from "@/pages/LoginPage";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { UserSettingsProvider } from "@/contexts/UserSettingsContext";
import { Loader2 } from "lucide-react";

// Lazy-loaded secondary pages (not needed on initial load).
// `TripDetails` is by far the heaviest — it transitively pulls in
// `ItineraryView` (~2500 lines), `GadjetView` (Cesium 3D globe), AI
// transport planner, etc. Keeping it lazy shaves ~MB off the initial JS
// chunk, which significantly speeds up first paint on /login and /.
const TripDetails = lazy(() => import("@/pages/TripDetails"));
const AuthGoogleReturn = lazy(() => import("@/pages/AuthGoogleReturn"));
const TravelersPage = lazy(() => import("@/pages/TravelersPage"));
const PaymentMethodsPage = lazy(() => import("@/pages/PaymentMethodsPage"));
const VisaDefinitionsPage = lazy(() => import("@/pages/VisaDefinitionsPage"));
const VendorsPage = lazy(() => import("@/pages/VendorsPage"));
const TripSharingPage = lazy(() => import("@/pages/TripSharingPage"));
const ShareAcceptancePage = lazy(() => import("@/pages/ShareAcceptancePage"));
const TravelResearchPage = lazy(() => import("@/pages/TravelResearchPage"));
const AdminPortal = lazy(() => import("@/pages/AdminPortal"));
const AdminLogsPage = lazy(() => import("@/pages/AdminLogsPage"));
const AdminAirportsPage = lazy(() => import("@/pages/AdminAirportsPage"));
const ManualEmailUploadPage = lazy(() => import("@/pages/ManualEmailUploadPage"));
const EmailInboxPage = lazy(() => import("@/pages/EmailInboxPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));

// API configuration - using versioned endpoints
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_VERSION = 'v1';

// Export versioned API URL for components
export const API = `${BACKEND_URL}/api/${API_VERSION}`;

// Legacy export for backward compatibility during migration
export const API_LEGACY = `${BACKEND_URL}/api`;

// Apply dark mode from localStorage immediately on module load (before first render)
try {
  const cached = localStorage.getItem('userSettings');
  const theme = cached ? JSON.parse(cached).theme : 'system';
  const effective = (theme === 'dark') ? 'dark'
    : (theme === 'light') ? 'light'
    : (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  if (effective === 'dark') document.documentElement.classList.add('dark');
} catch (e) { /* ignore */ }

// Suspense fallback — respects dark mode
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-sky-50/50 dark:from-gray-950 dark:to-gray-900">
    <div className="text-center">
      <Loader2 className="h-8 w-8 animate-spin text-sky-500 mx-auto mb-3" />
      <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <UserSettingsProvider>
      <div className="App">
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Critical path — eagerly loaded */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/google/return" element={<AuthGoogleReturn />} />
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/trip/:tripId" element={<ProtectedRoute><TripDetails /></ProtectedRoute>} />
              
              {/* Secondary pages — lazy loaded */}
              <Route path="/share/:token" element={<ShareAcceptancePage />} />
              <Route path="/admin" element={<ProtectedRoute><AdminPortal /></ProtectedRoute>} />
              <Route path="/admin/logs" element={<ProtectedRoute><AdminLogsPage /></ProtectedRoute>} />
              <Route path="/admin/airports" element={<ProtectedRoute><AdminAirportsPage /></ProtectedRoute>} />
              <Route path="/trips/:tripId/share" element={<ProtectedRoute><TripSharingPage /></ProtectedRoute>} />
              <Route path="/travelers" element={<ProtectedRoute><TravelersPage /></ProtectedRoute>} />
              <Route path="/payment-methods" element={<ProtectedRoute><PaymentMethodsPage /></ProtectedRoute>} />
              <Route path="/visa-requirements" element={<ProtectedRoute><VisaDefinitionsPage /></ProtectedRoute>} />
              <Route path="/vendors" element={<ProtectedRoute><VendorsPage /></ProtectedRoute>} />
              <Route path="/research" element={<ProtectedRoute><TravelResearchPage /></ProtectedRoute>} />
              <Route path="/upload-email" element={<ProtectedRoute><ManualEmailUploadPage /></ProtectedRoute>} />
              <Route path="/email-inbox" element={<ProtectedRoute><EmailInboxPage /></ProtectedRoute>} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </UserSettingsProvider>
  );
}

export default App;
